import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Activity, ArrowLeft, Phone, KeyRound, Loader2 } from "lucide-react";

/* Native OTP Input — guaranteed to open numeric keypad on Safari iOS */
function OtpInput({ value, onChange, length = 4 }) {
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus the hidden input when OTP step appears
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, length);
    onChange(val);
  };

  return (
    <div className="relative flex justify-center gap-3" data-testid="otp-input-container">
      {/* Visual boxes */}
      {Array.from({ length }).map((_, i) => (
        <div
          key={i}
          className={`
            w-14 h-14 rounded-xl border-2 flex items-center justify-center text-xl font-bold pointer-events-none
            ${i < value.length ? "border-[#B48C36] bg-[#FAF5EB]" : value.length === i ? "border-[#B48C36] bg-white" : "border-[#E8E4DC] bg-[#FAFAF9]"}
            text-[#1C1917]
          `}
        >
          {value[i] || ""}
        </div>
      ))}
      {/* Transparent input overlaid directly on the boxes — Safari opens keyboard on direct tap */}
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={length}
        value={value}
        onChange={handleChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-text"
        style={{ touchAction: "manipulation", fontSize: 16, caretColor: "transparent" }}
        data-testid="otp-input"
      />
    </div>
  );
}

export default function LoginPage() {
  const { user, loading: authLoading, sendOtp, login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authLoading && user) navigate("/dashboard", { replace: true });
  }, [user, authLoading, navigate]);

  const handleContinue = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length !== 10 || !"6789".includes(cleaned[0])) {
      setError("Enter a valid 10-digit Indian mobile number");
      return;
    }
    setLoading(true);
    try {
      const result = await sendOtp(cleaned);
      if (result.whitelisted) {
        // Auto-logged in, refresh auth state
        await checkAuth();
        return;
      }
      setStep("otp");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) return;
    setError("");
    setLoading(true);
    try {
      await login(phone, otp);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (otp.length === 4 && step === "otp" && !loading) {
      handleVerifyOtp({ preventDefault: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF5EB]">
        <Loader2 className="w-6 h-6 animate-spin text-[#B48C36]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF5EB] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
            <Activity className="w-5 h-5 text-[#B48C36]" />
          </div>
          <span
            className="font-heading text-xl font-black tracking-tight cursor-pointer"
            onClick={() => navigate("/")}
          >
            Bullion<span className="text-[#B48C36]">Track</span>
          </span>
        </div>

        {/* Card */}
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-6 sm:p-8 shadow-sm">
          {step === "phone" ? (
            <form onSubmit={handleContinue}>
              <div className="flex items-center gap-2 mb-6">
                <Phone className="w-5 h-5 text-[#B48C36]" />
                <h2 className="font-heading font-bold text-lg">Login with OTP</h2>
              </div>
              <p className="text-[#78716C] text-sm mb-5">
                Enter your mobile number to continue.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[#78716C] text-sm font-mono bg-[#FAF5EB] px-3 py-2 rounded-md border border-[#E8E4DC]">
                  +91
                </span>
                <Input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    setError("");
                  }}
                  placeholder="Enter mobile number"
                  className="bg-[#FAFAF9] border-[#E8E4DC] text-[#1C1917] placeholder:text-[#A8A29E] h-12 text-base"
                  autoFocus
                  data-testid="phone-input"
                />
              </div>
              {error && <p className="text-red-500 text-xs mb-3" data-testid="login-error">{error}</p>}
              <Button
                type="submit"
                disabled={loading || phone.length < 10}
                className="w-full bg-[#B48C36] hover:bg-[#9A7730] text-white font-bold h-12 text-base"
                style={{ touchAction: "manipulation" }}
                data-testid="send-otp-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <button
                type="button"
                className="flex items-center gap-1 text-[#78716C] active:text-[#1C1917] text-xs mb-5 py-2"
                style={{ touchAction: "manipulation" }}
                onClick={() => { setStep("phone"); setOtp(""); setError(""); }}
                data-testid="back-to-phone-btn"
              >
                <ArrowLeft className="w-3 h-3" /> Change number
              </button>
              <div className="flex items-center gap-2 mb-4">
                <KeyRound className="w-5 h-5 text-[#B48C36]" />
                <h2 className="font-heading font-bold text-lg">Enter OTP</h2>
              </div>
              <p className="text-[#78716C] text-sm mb-5">
                OTP sent to <span className="text-[#1C1917] font-mono">+91 {phone}</span>
              </p>

              {/* OTP Input */}
              <div className="mb-2">
                <OtpInput value={otp} onChange={setOtp} length={4} />
              </div>
              <p className="text-center text-[11px] text-[#B48C36] mb-5">Tap the boxes to enter OTP</p>

              {error && <p className="text-red-500 text-xs mb-3 text-center" data-testid="otp-error">{error}</p>}
              <Button
                type="submit"
                disabled={loading || otp.length < 4}
                className="w-full bg-[#B48C36] hover:bg-[#9A7730] text-white font-bold h-12 text-base"
                style={{ touchAction: "manipulation" }}
                data-testid="verify-otp-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Login"}
              </Button>
            </form>
          )}
        </div>

        <p className="text-[#A8A29E] text-[10px] text-center mt-5 leading-relaxed max-w-[320px] mx-auto">
          By logging in, you agree to our{" "}
          <a href="/terms" className="text-[#78716C] underline">Terms of Service</a> and{" "}
          <a href="/privacy" className="text-[#78716C] underline">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}
